import React, { useMemo } from 'react'
import { ControllingRow, Gewerk } from '@myapp/contract'
import {
  useControllingFilters,
  ControllingSortField,
  ControllingSortDirection,
} from '../../../../features/controlling/filters'
import { useGewerkeInfiniteQuery } from '../../../../entities/gewerk'
import { DataTable, Pagination, SortDirection } from '../../../../shared/ui'
import { routes } from 'src/shared/config/routes'
import { useNavigate } from 'react-router-dom'
import {
  createControllingColumns,
  renderSollIst,
  fmtDeviation,
  deviationClass,
  fmtNum,
} from '../model/columns'

export const ControllingList: React.FC = () => {
  const { data, isLoading, isFetching, error, filters, setFilters, page, setPage } =
    useControllingFilters();

  const { data: gewerkeData } = useGewerkeInfiniteQuery({ limit: 500 })
  const gewerkLabels = useMemo(() => {
    const m = new Map<number, string>()
    const list: Gewerk[] = gewerkeData?.items ?? []
    list.forEach((g) => m.set(g.id, g.text))
    return m
  }, [gewerkeData])

  const columns = useMemo(
    () => createControllingColumns({ gewerkLabels }),
    [gewerkLabels],
  )

  const navigate = useNavigate()
  const items = data?.items ?? []
  const meta = data?.meta
  const totalPages = meta ? Math.ceil(meta.total / meta.limit) : 0

  const totals = useMemo(() => {
    const agg = data?.aggregates
    if (!agg) return null
    return {
      plannedWork:   agg.totalPlannedWorkHours,
      actualWork:    agg.totalActualWorkHours,
      plannedTravel: agg.totalPlannedTravelHours,
      actualTravel:  agg.totalActualTravelHours,
      plannedTotal:  agg.totalPlannedHours,
      actualTotal:   agg.totalActualHours,
      avgDeviation:  agg.deviation,
      wertEuro:      agg.totalWertEuro,
    }
  }, [data?.aggregates])

  const handleSort = (field: string, direction: SortDirection) => {
    void setFilters({
      sortField: field as ControllingSortField,
      sortDirection: direction as ControllingSortDirection,
      page: 1,
    })
  }

  const C = 'px-1.5 py-1.5'
  const totalCellByKey: Record<string, React.ReactNode> = totals
    ? {
        workOrderId: (
          <>
            <span className="font-medium text-gray-700">Gesamt</span>
            <br />
            <span className="text-gray-500">{meta?.total ?? 0} Aufträge</span>
          </>
        ),
        workHours:   renderSollIst(totals.plannedWork, totals.actualWork),
        travelHours: renderSollIst(totals.plannedTravel, totals.actualTravel),
        totalHours:  renderSollIst(totals.plannedTotal, totals.actualTotal),
        deviation: (
          <span className={`whitespace-nowrap font-medium ${deviationClass(totals.avgDeviation)}`}>
            {fmtDeviation(totals.avgDeviation)}
          </span>
        ),
        wertEuro: (
          <span className="whitespace-nowrap font-medium">{fmtNum(totals.wertEuro)} €</span>
        ),
      }
    : {}
  const totalsRow = totals ? (
    <tr className="bg-gray-100 border-t-2 border-gray-200 text-gray-600 text-xs">
      {columns.map((col) => (
        <td key={col.key} className={`${C} align-top`}>
          {totalCellByKey[col.key] ?? <span className="text-gray-400">–</span>}
        </td>
      ))}
    </tr>
  ) : undefined
  return (
    <div className="flex flex-col">
      <DataTable<ControllingRow>
        columns={columns}
        rows={items}
        getRowKey={(r, i) => `${r.workOrderId}-${i}`}
        isLoading={isLoading}
        isFetching={isFetching}
        error={error}
        sort={{ field: filters.sortField, direction: filters.sortDirection }}
        onSortChange={handleSort}
        emptyMessage="Keine Ergebnisse"
        footerRow={totalsRow}
        compact
        minWidth={1280}
        tableClassName="[&_th]:!px-1.5 [&_td]:!px-1.5 [&_th]:!py-2 [&_td]:!py-2"
        onRowClick={r=> navigate(routes.task.detail(r.workOrderId) )}
      />
      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={(p) => void setPage(p)}
        isFetching={isFetching}
        disabled={isLoading}
      />
    </div>
  )
}
